<template>
  <div class="">
    <MainTitle el="h1" class="mt-8 text-primary-A900 text-center text-1.6xl lg:text-2xl-1" align="left">
      <template #before>
        {{ $t('aboutUs') }}
      </template>
      {{ $t('us') }}
    </MainTitle>

    <nav class="w-full bg-white sticky top-0 md:mt-12 mt-6 z-30">
      <ul
        class="container flex flex-nowrap justify-start gap-8 sm:gap-12 px-5 md:px-2 overflow-x-auto hide-scrollbar border-b border-gray-100"
      >
        <li v-for="tab in tabs" :key="tab.key" class="relative">
          <a
            :href="`#${tab.key}`"
            class="inline-block py-4 text-primary-A900 font-normal text-tiny sm:text-base outline-none transition-colors whitespace-nowrap relative"
            :class="activeTab === tab.key ? 'font-bold' : 'hover:text-primary-A700'"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
            <span
              v-if="activeTab === tab.key"
              class="block absolute left-0 right-0 bottom-0 h-[2px] bg-primary-700 transition-all"
            ></span>
          </a>
        </li>
      </ul>
    </nav>
    <div id="about" class="mt-8 mx-auto scroll-mt-8">
      <!-- About Us -->
      <div class="w-full max-w-7xl mx-auto px-4 md:px-0">
        <div class="p-4 mx-auto mt-8">
          <div class="flex gap-3 mb-4 md:hidden">
            <NuxtImg src="/about-us-left.avif" alt="" class="rounded-2xl w-1/2 h-full object-cover" />
            <NuxtImg src="/about-us-right.avif" alt="" class="rounded-2xl w-1/2 object-cover" />
          </div>

          <div class="hidden md:flex flex-col md:flex-row md:items-start gap-8 md:gap-14">
            <NuxtImg
              src="/about-us-left.avif"
              alt=""
              class="rounded-2xs w-full max-w-[260px] h-auto object-cover mb-4 md:mb-0"
            />
            <section v-purify="about" class="text-primary-A900 flex-1 flex flex-col gap-y-6 md:gap-y-4" />
            <NuxtImg
              src="/about-us-right.avif"
              alt=""
              class="rounded-2xs w-full max-w-[260px] h-auto object-cover mt-4 md:mt-0"
            />
          </div>

          <section v-purify="about" class="block md:hidden text-primary-A900 text-[15px] leading-[1.6]" />
        </div>

        <!-- Two-Card Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-18 md:mt-20 mt-14">
          <!-- Carrier Card -->
          <div class="space-y-4 md:space-y-6">
            <NuxtImg src="/carrier-about.avif" alt="Carrier AC" class="w-full rounded-2xs object-cover h-auto" />
            <p class="text-base leading-relaxed text-secondary-700">
              {{ $t('carrierText') }}
            </p>
          </div>
          <!-- Midea Card -->
          <div class="relative space-y-0">
            <NuxtImg src="/midea-about.avif" alt="Midea AC" class="w-full rounded-2xs object-cover h-auto" />
            <div class="absolute inset-0 rounded-2xs bg-gradient-to-t from-black/60 to-transparent"></div>
            <div class="absolute bottom-0 left-0 w-full p-4 md:p-6 text-white">
              <p class="text-base leading-relaxed">
                {{ $t('mideaText') }}
              </p>
            </div>
          </div>
        </div>

        <div class="md:grid md:grid-cols-2 md:gap-18 md:items-center md:mt-20 mt-14">
          <!-- MOBILE: title then image then text -->
          <div class="block md:hidden mb-4">
            <MainTitle el="h2" class="text-primary-A900 text-1.6xl lg:text-2xl-1 mb-8 md:mb-12" align="center">
              <template #before>
                {{ $t('quality') }}
              </template>
              {{ $t('policy') }}
            </MainTitle>
            <NuxtImg src="/quality.avif" alt="Quality Policy" class="w-full object-cover h-auto rounded-2xs mb-4" />
            <p class="text-base leading-relaxed text-secondary-700 mb-2">
              {{ $t('qualityParagraph1') }}
            </p>
            <p class="text-base leading-relaxed text-secondary-700">
              {{ $t('qualityParagraph2') }}
            </p>
          </div>

          <!-- Quality Policy -->
          <div class="hidden md:block">
            <MainTitle
              el="h2"
              class="text-primary-A900 text-1.6xl md:text-left mb-8 md:mb-12 lg:text-2xl-1"
              align="center"
            >
              <template #before>
                {{ $t('quality') }}
              </template>
              {{ $t('policy') }}
            </MainTitle>
            <p class="text-base leading-relaxed text-secondary-700 mb-4">
              {{ $t('qualityParagraph1') }}
            </p>
            <p class="text-base leading-relaxed text-secondary-700">
              {{ $t('qualityParagraph2') }}
            </p>
          </div>
          <div class="hidden md:block overflow-hidden rounded-2xs">
            <NuxtImg src="/quality.avif" alt="Quality Policy" class="w-full object-cover h-auto" />
          </div>
        </div>

        <!-- Why Shop From Miraco -->
        <div class="mt-14 md:mt-20">
          <MainTitle
            id="why"
            class="scroll-mt-12 md:scroll-mt-20 text-primary-A900 mb-8 md:mb-12 text-center text-1.6xl lg:text-2xl-1"
            align="center"
          >
            <template #before>
              {{ $t('shopFrom') }}
            </template>
            {{ $t('title') }}
          </MainTitle>
          <ul
            class="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:flex lg:justify-between gap-x-4 md:gap-x-8 gap-y-6 md:gap-y-8 mt-8"
          >
            <li class="flex flex-col text-center items-center px-2">
              <svg-icon name="graph/deals" width="40" height="40" class="mb-2" />
              <h3 class="text-primary-700 text-lg-1 font-bold">
                {{ $t('info1') }}
              </h3>
              <p class="text-secondary-700 text-base">
                {{ $t('desc1') }}
              </p>
            </li>
            <li class="flex flex-col text-center items-center px-2">
              <svg-icon name="graph/wallet" width="40" height="40" class="mb-2" />
              <h3 class="text-primary-700 text-lg-1 font-bold">
                {{ $t('info2') }}
              </h3>
              <p class="text-secondary-700 text-base">
                {{ $t('desc2') }}
              </p>
            </li>
            <li class="flex flex-col text-center items-center px-2">
              <svg-icon name="graph/wrench" width="40" height="40" class="mb-2" />
              <h3 class="text-primary-700 text-lg-1 font-bold">
                {{ $t('info3') }}
              </h3>
              <p class="text-secondary-700 text-base">
                {{ $t('desc3') }}
              </p>
            </li>
            <li class="flex flex-col text-center items-center px-2">
              <svg-icon name="graph/measurements" width="40" height="40" class="mb-2" />
              <h3 class="text-primary-700 text-lg-1 font-bold">
                {{ $t('info4') }}
              </h3>
              <p class="text-secondary-700 text-base">
                {{ $t('desc4') }}
              </p>
            </li>
            <li class="flex flex-col text-center items-center px-2">
              <svg-icon name="graph/payments" width="40" height="40" class="mb-2" />
              <h3 class="text-primary-700 text-lg-1 font-bold">
                {{ $t('info5') }}
              </h3>
              <p class="text-secondary-700 text-base">
                {{ $t('desc5') }}
              </p>
            </li>
          </ul>
        </div>

        <!-- PCB Factory -->
        <div id="pcb" class="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-14 items-center mt-14 md:mt-20 scroll-mt-12">
          <div class="overflow-hidden rounded-2xs">
            <MainTitle
              el="h2"
              class="text-primary-A900 text-1.6xl md:hidden lg:text-2xl-1 mb-8 md:mb-12"
              align="center"
            >
              <template #before>
                {{ $t('pcb') }}
              </template>
              {{ $t('factory') }}
            </MainTitle>
            <NuxtImg src="/pcb-about.avif" alt="PCB Factory" class="w-full object-cover h-auto" />
          </div>
          <div class="space-y-3 md:space-y-4 text-secondary-700 mt-4 md:mt-0">
            <MainTitle el="h2" class="text-primary-A900 text-1.6xl hidden md:block lg:text-2xl-1" align="left">
              <template #before>
                {{ $t('pcb') }}
              </template>
              {{ $t('factory') }}
            </MainTitle>
            <div v-purify="pcp" />
          </div>
        </div>

        <!-- Labs -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-14 items-center mt-14 md:mt-20">
          <!-- Image first on mobile, second on desktop -->
          <div class="order-1 md:order-2 overflow-hidden rounded-2xs mt-4 md:mt-0">
            <NuxtImg src="/pcb-about2.avif" alt="Testing Labs" class="w-full object-cover h-auto" />
          </div>

          <!-- Text content: paragraph + timeline -->
          <div class="order-2 md:order-1 space-y-4 md:space-y-6 text-secondary-700">
            <div v-purify="pcp2" />

            <div class="hidden md:grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              <div class="space-y-1">
                <h3 class="text-primary-A900 font-bold text-2xl md:text-3.5xl">1992</h3>
                <p class="text-sm">
                  <span class="font-bold text-primary-A900 text-lg">{{ $t('timeline1992Title') }} </span><br />
                  {{ $t('timeline1992Desc') }}
                </p>
              </div>
              <div class="space-y-1">
                <h3 class="text-primary-A900 font-bold text-2xl md:text-3.5xl">2005</h3>
                <p class="text-sm">
                  <span class="font-bold text-primary-A900 text-lg">{{ $t('timeline2005Title') }} </span><br />
                  {{ $t('timeline2005Desc') }}
                </p>
              </div>
              <div class="space-y-1">
                <h3 class="text-primary-A900 font-bold text-2xl md:text-3.5xl">2012</h3>
                <p class="text-sm">
                  <span class="font-bold text-primary-A900 text-lg">{{ $t('timeline2012Title') }}</span
                  ><br />
                  {{ $t('timeline2012Desc') }}
                </p>
              </div>
              <div class="space-y-1">
                <h3 class="text-primary-A900 font-bold text-2xl md:text-3.5xl">2017</h3>
                <p class="text-sm">
                  <span class="font-bold text-primary-A900 text-lg">{{ $t('timeline2017Title') }}</span
                  ><br />
                  {{ $t('timeline2017Desc') }}
                </p>
              </div>
            </div>
            <!--mobile layout-->
            <div class="grid grid-cols-2 gap-x-4 gap-y-6 md:hidden mt-8">
              <div class="space-y-1">
                <h3 class="text-primary-A900 font-bold text-2xl">1992</h3>
                <p class="text-primary-A900 font-bold text-base leading-tight">
                  {{ $t('timeline1992Title') }}
                </p>
                <p class="text-secondary-700 text-sm leading-snug">
                  {{ $t('timeline1992Desc') }}
                </p>
              </div>

              <div class="space-y-1">
                <h3 class="text-primary-A900 font-bold text-2xl">2005</h3>
                <p class="text-primary-A900 font-bold text-base leading-tight">
                  {{ $t('timeline2005Title') }}
                </p>
                <p class="text-secondary-700 text-sm leading-snug">
                  {{ $t('timeline2005Desc') }}
                </p>
              </div>

              <div class="space-y-1">
                <h3 class="text-primary-A900 font-bold text-2xl">2012</h3>
                <p class="text-primary-A900 font-bold text-base leading-tight">
                  {{ $t('timeline2012Title') }}
                </p>
                <p class="text-secondary-700 text-sm leading-snug">
                  {{ $t('timeline2012Desc') }}
                </p>
              </div>

              <div class="space-y-1">
                <h3 class="text-primary-A900 font-bold text-2xl">2017</h3>
                <p class="text-primary-A900 font-bold text-base leading-tight">
                  {{ $t('timeline2017Title') }}
                </p>
                <p class="text-secondary-700 text-sm leading-snug">
                  {{ $t('timeline2017Desc') }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- About Slider -->
      <div
        v-if="missionAndVisionBanners.length"
        class="slider-container w-full max-w-[1366px] mx-auto overflow-hidden mt-14 md:mt-20"
      >
        <ExpandedCutoutsOffersSlider :items="missionAndVisionBanners" />
      </div>
      <!-- Our Labs -->
      <section id="labs" class="mt-14 md:mt-20 scroll-mt-12 md:scroll-mt-20">
        <MainTitle class="text-primary-A900 text-center text-1.6xl lg:text-2xl-1" align="center">
          <template #before>
            {{ $t('our') }}
          </template>
          {{ $t('labs') }}
        </MainTitle>
        <div class="px-2 sm:px-20">
          <OurLabsSlider :items="labsItems" />
        </div>
      </section>
      <!-- Our Standards -->
      <div class="container px-4 md:px-0">
        <section class="mt-2 md:mt-8 max-w-7xl mx-auto flex items-center justify-between">
          <MainTitle class="text-primary-A900 text-start text-1.6xl lg:text-2xl-1" align="left">
            <template #before>
              {{ $t('our') }}
            </template>
            {{ $t('standards') }}
          </MainTitle>

          <ul class="grid grid-cols-1 mt-8 md:mt-12 gap-y-6 md:gap-y-8 gap-x-4 md:gap-x-6 w-full">
            <!-- Safety -->
            <li class="flex items-center justify-end gap-3 md:gap-4">
              <svg-icon name="shield" width="40" height="40" class="text-primary-700 flex-shrink-0" />
              <div class="flex flex-col">
                <span class="text-primary-A1100 font-bold text-lg">{{ $t('iso17025Subtitle') }}</span>
                <a
                  href="/pdfs/miraco-laboratories-accreditation-certificate-iso.pdf"
                  target="_blank"
                  class="mt-1 text-primary-800 underline"
                >
                  {{ $t('downloadIsoCertificate') }}
                </a>
              </div>
            </li>
          </ul>
        </section>

        <!-- Ethics & Compliance -->
        <div
          id="ethics"
          class="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-14 items-center mt-14 md:mt-20 scroll-mt-12 md:scroll-mt-20"
        >
          <!-- Left image -->
          <div class="overflow-hidden rounded-2xs">
            <MainTitle
              el="h2"
              class="text-primary-A900 text-1.6xl md:hidden mb-8 md:mb-12 lg:text-2xl-1"
              align="center"
            >
              <template #before>
                {{ $t('ethics') }}
              </template>
              {{ $t('compliance') }}
            </MainTitle>
            <NuxtImg src="/compliance-about.avif" alt="Ethics & Compliance" class="w-full object-cover h-auto" />
          </div>

          <!-- Right copy + buttons -->
          <div class="flex flex-col md:space-y-6 md:mt-0">
            <MainTitle el="h2" class="text-primary-A900 hidden md:block text-1.6xl lg:text-2xl-1" align="left">
              <template #before>
                {{ $t('ethics') }}
              </template>
              {{ $t('compliance') }}
            </MainTitle>

            <div v-purify="ethicsAndCompliance" class="text-secondary-700" />
            <!-- mobile -->
            <ul class="flex flex-row items-center w-full mt-6 md:hidden mb-7">
              <li v-for="link in ethicsLinks" :key="link.title" class="relative flex w-full justify-center">
                <div
                  class="relative flex flex-col justify-center items-center bg-secondary-300 w-45 h-42 rounded-2xl overflow-visible"
                >
                  <span class="text-center text-primary-700 font-normal text-tiny">
                    {{ link.title }}
                  </span>
                  <a
                    :href="link.link"
                    target="_blank"
                    class="absolute left-1/2 -translate-x-1/2 bottom-[-40px] z-10"
                    aria-label="Download {{ link.title }}"
                  >
                    <span
                      class="z-1 ml-auto lg:-ml-16 border-3 border-white mx-auto bg-coloration-secondary rounded-full w-18 h-18 flex items-center justify-center"
                    >
                      <svg-icon name="download" width="28" height="30" class="text-white" />
                    </span>
                  </a>
                </div>
              </li>
            </ul>

            <!-- desktop -->
            <ul class="hidden md:grid md:grid-cols-2 gap-6 md:mt-7">
              <li v-for="link in ethicsLinks" :key="link.title" class="flex items-center bullet-list">
                <a
                  :href="link.link"
                  target="_blank"
                  class="flex-1 bullet bg-secondary-300 rounded-xl text-primary-700 flex items-center gap-x-4 h-24 md:h-auto pl-8 md:pl-[30px] lg:pr-[64px] md:py-[38px] relative"
                  aria-label="Download {{ link.title }}"
                >
                  <span class="text-base font-medium">{{ link.title }}</span>
                </a>
                <div
                  class="z-1 ml-auto lg:-ml-16 border-3 border-white mx-auto bg-coloration-secondary rounded-full w-20 h-20 flex items-center justify-center"
                >
                  <svg-icon name="download" width="20" height="16" class="text-white" />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- EH&S Policy -->
      <section
        class="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-start mt-14 md:mt-20 px-4 md:px-0 max-w-7xl mx-auto"
      >
        <div class="space-y-4 md:space-y-6">
          <MainTitle
            el="h2"
            class="text-primary-A900 md:text-left mb-8 md:mb-12 text-1.6xl lg:text-2xl-1"
            align="center"
          >
            <template #before>
              {{ $t('our') }}
            </template>
            {{ $t('ehsPolicy') }}
          </MainTitle>
          <div class="overflow-hidden md:hidden rounded-2xs mt-4 md:mt-0">
            <NuxtImg src="/policy-about.avif" :alt="$t('ehsPolicy')" class="w-full object-cover h-auto" />
          </div>
          <div v-purify="ehAndS" />
        </div>
        <!-- Illustration -->
        <div class="overflow-hidden hidden md:block rounded-2xs mt-4 md:mt-0">
          <NuxtImg src="/policy-about.avif" :alt="$t('ehsPolicy')" class="w-full object-cover h-auto" />
        </div>
      </section>

      <!-- Certified In -->

      <section class="mt-14 md:mt-20 px-4 md:px-0 mx-auto text-center container">
        <MainTitle class="text-primary-A900 text-center text-xl lg:text-2xl-1" align="center">
          <template #before>
            {{ $t('certifiedInPrefix') }}
          </template>
          {{ $t('certifiedInSuffix') }}
        </MainTitle>
        <ul
          class="md:mt-12 mt-8 flex overflow-x-auto gap-4 md:grid md:grid-cols-3 lg:grid-cols-5 md:gap-8 w-full hide-scrollbar sm:overflow-x-visible"
        >
          <li
            class="flex-shrink-0 w-56 max-w-[80vw] flex flex-col items-center md:flex-shrink-0 md:w-auto md:max-w-none"
          >
            <img src="/assets/icons/iso-1.svg" alt="ISO 9001:2015" class="w-14 md:w-20 h-14 md:h-20" />
            <h3 class="mt-2 md:mt-4 text-primary-A1100 font-bold">
              {{ $t('iso9001Title') }}
            </h3>
            <p class="text-secondary-700 text-xs md:text-sm">
              {{ $t('iso9001Subtitle') }}
            </p>

            <a
              href="/pdfs/quality-system-certificate-iso.pdf"
              target="_blank"
              class="text-primary-800 underline text-xs md:text-sm"
            >
              {{ $t('viewCertificate') }}
            </a>
          </li>
          <li
            class="flex-shrink-0 w-56 max-w-[80vw] flex flex-col items-center md:flex-shrink-0 md:w-auto md:max-w-none"
          >
            <img src="/assets/icons/iso2.svg" alt="ISO 14001:2015" class="w-14 md:w-20 h-14 md:h-20" />
            <h3 class="mt-2 md:mt-4 text-primary-A1100 font-bold">
              {{ $t('iso14001Title') }}
            </h3>
            <p class="text-secondary-700 text-xs md:text-sm">
              {{ $t('iso14001Subtitle') }}
            </p>

            <a
              href="/pdfs/environmental-management-system-certificate.pdf"
              class="text-primary-800 mt-2 underline text-xs md:text-sm"
              target="_blank"
            >
              {{ $t('viewCertificate') }}
            </a>
          </li>
          <li
            class="flex-shrink-0 w-56 max-w-[80vw] flex flex-col items-center md:flex-shrink-0 md:w-auto md:max-w-none"
          >
            <img src="/assets/icons/iso3.svg" alt="ISO 45001:2015" class="w-14 md:w-20 h-14 md:h-20" />
            <h3 class="mt-2 md:mt-4 text-primary-A1100 font-bold">
              {{ $t('iso45001Title') }}
            </h3>
            <p class="text-secondary-700 text-xs md:text-sm">
              {{ $t('iso45001Subtitle') }}
            </p>

            <a
              href="/pdfs/occupational-health-and-safety-certificate.pdf"
              class="text-primary-800 mt-2 underline text-xs md:text-sm"
              target="_blank"
            >
              {{ $t('viewCertificate') }}
            </a>
          </li>
          <li
            class="flex-shrink-0 w-56 max-w-[80vw] flex flex-col items-center md:flex-shrink-0 md:w-auto md:max-w-none"
          >
            <img src="/assets/icons/iso4.svg" alt="ISO/IEC 17025:2017" class="w-14 md:w-20 h-14 md:h-20" />
            <h3 class="mt-2 md:mt-4 text-primary-A1100 font-bold">
              {{ $t('iso17025Title') }}
            </h3>
            <p class="text-secondary-700 text-xs md:text-sm">
              {{ $t('iso17025Subtitle') }}
            </p>

            <a
              href="/pdfs/miraco-laboratories-accreditation-certificate-iso.pdf"
              target="_blank"
              class="text-primary-800 mt-2 underline text-xs md:text-sm"
            >
              {{ $t('viewCertificate') }}
            </a>
          </li>
          <li
            class="flex-shrink-0 w-56 max-w-[80vw] flex flex-col items-center md:flex-shrink-0 md:w-auto md:max-w-none"
          >
            <img src="/assets/icons/iso5.svg" alt="ISO/IEC 27001:2013" class="w-14 md:w-20 h-14 md:h-20" />
            <h3 class="mt-2 md:mt-4 text-primary-A1100 font-bold">
              {{ $t('iso27001Title') }}
            </h3>
            <p class="text-secondary-700 text-xs md:text-sm">
              {{ $t('iso27001Subtitle') }}
            </p>

            <a
              href="/pdfs/information-security-management-system-certificate-iso.pdf"
              target="_blank"
              class="text-primary-800 mt-2 underline text-xs md:text-sm"
            >
              {{ $t('viewCertificate') }}
            </a>
          </li>
        </ul>
      </section>
    </div>

    <section class="container mx-auto px-4 md:px-0 max-w-7xl mt-14 md:mt-20">
      <MainTitle class="text-primary-A900 text-center text-1.6xl mb-8 md:mb-12 lg:text-2xl-1" align="center">
        <template #before>
          {{ $t('ehs') }}
        </template>
        {{ $t('drivenBy') }}
      </MainTitle>
    </section>
    <div v-if="ehAndSPolicyBanners.length" class="slider-container max-w-[1366px] w-full mx-auto overflow-hidden">
      <ExpandedCutoutsOffersSlider :items="ehAndSPolicyBanners" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { NuxtImg } from '#components';
import { throttle } from 'lodash-es';

const { t: $t } = useI18n({ useScope: 'local' });

const tabs = computed(() => [
  { key: 'about', label: $t('tabs.about') },
  { key: 'why', label: $t('tabs.why') },
  { key: 'pcb', label: $t('tabs.pcb') },
  { key: 'labs', label: $t('tabs.labs') },
  { key: 'ethics', label: $t('tabs.ethics') },
]);

const activeTab = ref('about');

const { about, ehAndS, ehAndSPolicyBanners, ethicsAndCompliance, pcp, pcp2, missionAndVisionBanners } =
  useAboutUsPageContent();
const { formatPageTitle } = useSeoFormatting();
useSeoMeta({
  title: formatPageTitle($t('aboutUs') as string),
});

const ethicsLinks = computed(() => [
  {
    title: $t('codeEthicsEn'),
    link: $t('ethicsPdfEn'),
  },
  {
    title: $t('codeEthicsAr'),
    link: $t('ethicsPdfAr'),
  },
]);

const labsItems = computed(() => [
  {
    id: 1,
    title: $t('labsItems.item1.title'),
    description: $t('labsItems.item1.description'),
  },
  {
    id: 2,
    title: $t('labsItems.item2.title'),
    description: $t('labsItems.item2.description'),
  },
  {
    id: 3,
    title: $t('labsItems.item3.title'),
    description: $t('labsItems.item3.description'),
  },
  {
    id: 4,
    title: $t('labsItems.item4.title'),
    description: $t('labsItems.item4.description'),
  },
]);

onMounted(() => {
  const onScroll = throttle(() => {
    let found = false;
    for (let i = tabs.value.length - 1; i >= 0; i--) {
      if (!tabs.value[i]?.key) continue;

      const key = tabs.value[i]?.key;
      if (key) {
        const el = document.getElementById(key);
        if (el && window.scrollY + 120 >= el.offsetTop && tabs.value[i]?.key) {
          if (tabs.value[i]?.key) {
            activeTab.value = tabs.value[i]?.key ?? '';
            found = true;
            break;
          }
        }
      }
      if (!found) activeTab.value = tabs.value[0]?.key ?? '';
    }
  }, 200); // Throttle the scroll event to run every 200ms

  window.addEventListener('scroll', onScroll);

  onUnmounted(() => {
    window.removeEventListener('scroll', onScroll);
  });
});
</script>

<i18n lang="json">
{
  "en": {
    "home": "Home",
    "aboutUs": "About",
    "us": "Us",
    "title": "Miraco",
    "info1": "Exclusive Offers",
    "desc1": "Best Deals for Air Conditioners",
    "info2": "Free Shipping",
    "desc2": "Free delivery at your doorstep",
    "info3": "Free Installation",
    "desc3": "Free Installation when you shop online",
    "info4": "Find the Right Fit",
    "desc4": "Get the most compatible product for your room",
    "info5": "Payment Facilities",
    "desc5": "Pay in Cash, Credit or Debit Cards",
    "shopFrom": "Why Shop From",
    "tabs": {
      "about": "About",
      "why": "Why Miraco?",
      "pcb": "PCB Factory",
      "labs": "Laboratories",
      "ethics": "Ethics & Compliance"
    },

    "carrierText": "Carrier is a global leader in smart climate and energy solutions, driven by innovation and a customer-first approach. With a world-class team and performance-driven culture, we deliver long-term value and lead the market through strategic growth.",
    "mideaText": "Founded in 1968, Midea offers a wide range of home appliances, specializing in air treatment, refrigeration, laundry, kitchen, water, floor care & lighting solutions. Renowned globally for innovation & quality, Midea continues to enhance everyday living.",

    "quality": "Quality",
    "policy": "Policy",
    "qualityParagraph1": "We’re committed to delivering high-quality HVAC solutions that meet customer expectations, on time and within budget, while complying with all regulations.",
    "qualityParagraph2": "By engaging our team, applying international standards, and embracing continuous improvement, we drive efficiency, sustainability, & long-term success—maintaining our position as a leading provider in the industry.",

    "pcb": "PCB",
    "factory": "Factory",
    "pcbParagraph1": "We aspire to produce the highest quality electronic control boards. We aim to be a first-class factory serving the Middle East and Africa.",
    "pcbParagraph2": "Founded in February 2022, the Miraco PCB factory serves as the manufacturing base for the Midea joint venture in Egypt, specializing in electronic controllers and display boards. The facility features cleanrooms with a constant temperature and humidity rated at 300,000, along with a professional anti-static ground. It is equipped with fully automated SMT production lines, fully automated insertion production lines, and 2 DIP production lines.",
    "pcbParagraph3": "We prioritize value optimization and are committed to continuous engineering development to achieve customer satisfaction and delivery requirements.",
    "pcbParagraph4": "We engage in various business activities guided by our four strategic pillars of technology leadership.",

    "labs": "Labs",
    "history": "History",
    "labsParagraph": "We are proud to be Egypt’s first ISO 17025-accredited lab for air conditioning testing since 2005. Our expert labs support both R&D and quality control by testing random samples across Miraco’s product lines. We ensure every unit meets the highest standards of efficiency, safety, and reliable 24/7 performance throughout the year.",
    "timeline1992Title": "Starting Point",
    "timeline1992Desc": "Miraco Labs were established to serve testing of A/C Units",
    "timeline2005Title": "Accreditation",
    "timeline2005Desc": "First ISO 17025 accredited labs in Egypt by EGAC",
    "timeline2012Title": "Labs",
    "timeline2012Desc": "First Labs in Egypt to get ESMA recognition certificate",
    "timeline2017Title": "Safety & Calibration Labs",
    "timeline2017Desc": "Introduced Calibration & product safety lab based on IEC 60335-1&2",
    "aboutSlider": {
      "item1": {
        "title": "What Drives Us",
        "description": "Is leveraging our core strengths, we deliver air-conditioning testing, calibration, and safety services for Miraco — with a firm commitment to top-tier quality, minimal uncertainty, and the expertise of our specialized team using the latest technology."
      },
      "item2": {
        "title": "What Drives Us",
        "description": ""
      },
      "item3": {
        "title": "",
        "description": ""
      }
    },

    "our": "Our",
    "ehs": "EH&S",
    "standards": "Standards",
    "testing": "Testing",
    "downloadTestingDocs": "Download Testing Docs",
    "calibration": "Calibration",
    "downloadCalibrationDocs": "Download Calibration Docs",
    "safety": "Safety",
    "downloadSafetyDocs": "Download Safety Docs",

    "ethics": "Ethics &",
    "compliance": "Compliance",
    "ethicsParagraph1": "Miraco (MISR Refrigeration & Air Conditioning Mfg. Co. S.A.E) is committed to implement the highest standards of ethics, compliance and combating corruption, bribe, and money laundering. Ethics and Compliance organization at Miraco includes Violations Reporting Program and an Ethics and Compliance Officer who provides training, investigates allegations, and conducts Enterprise Risk Management.",
    "ethicsParagraph2": "We expect our employees to perform their work in a legal, ethical, and safe manner. Build trust, show respect, perform with integrity, and protect the company reputation and growth.",
    "codeEthicsEn": "Code Of Ethics – EN",
    "ethicsPdfEn": "/pdfs/code-of-ethics-en.pdf",
    "codeEthicsAr": "Code Of Ethics – AR",
    "ethicsPdfAr": "/pdfs/code-of-ethics-ar.pdf",

    "ehsPolicy": "EH&S Policy",
    "ehsParagraph1": "At Miraco (MISR REFRIGERATION & AIR CONDITIONING MFG. Co. S.A.E), the safety of our employees and the environment is our top priority. We are committed to maintaining a workplace free from hazards and pollution, where employees are safe, and our products and services are both safe and environmentally responsible.",
    "ehsParagraph2": "To achieve this, each Miraco business unit in Egypt is required to:",
    "ehsBullet1": "Comply with all local EHS laws, regulations, and corporate standards",
    "ehsBullet2": "Foster a culture of safety and environmental responsibility through active employee participation",
    "ehsBullet3": "Systematically assess, prevent, and control safety and environmental risks",
    "ehsBullet4": "Integrate EHS goals into business planning",
    "ehsBullet5": "Allocate resources to support, monitor, and maintain accountability for EHS performance",
    "ehsBullet6": "Investigate incidents and take corrective and preventive action",
    "ehsBullet7": "Prevent pollution in accordance with ISO 14001:2015",
    "ehsBullet8": "Continuously improve to reduce risks and environmental impact",

    "certifiedInPrefix": "We’re proudly",
    "certifiedInSuffix": "certified in",
    "iso9001Title": "ISO 9001:2015",
    "iso9001Subtitle": "Quality Management",
    "iso14001Title": "ISO 14001:2015",
    "iso14001Subtitle": "Environmental Management",
    "iso45001Title": "ISO 45001:2018",
    "iso45001Subtitle": "Occupational Health & Safety",
    "iso17025Title": "ISO/IEC 17025:2017",
    "iso17025Subtitle": "Laboratory Accreditation",
    "iso27001Title": "ISO/IEC 27001:2013",
    "iso27001Subtitle": "Information Security Management",

    "drivenBy": "Is Driven By",
    "drivenByItems": {
      "item1": {
        "title": "The Purpose We Serve",
        "description": "Is to sustain the outstanding EHS performance in terms of reducing number of injuries and promoting our employees’ health conditions and preventing any environmental pollution."
      },
      "item2": { "title": "", "description": "" },
      "item3": { "title": "", "description": "" },
      "item4": { "title": "", "description": "" }
    },

    "labsItems": {
      "item1": {
        "title": "PSYCHOMETRIC 1 LAB",
        "description": "Based on Air Enthalpy Test Method with Capacity Range up to 5 TR, and airflow from 200 to 2200 CFM."
      },
      "item2": {
        "title": "PSYCHOMETRIC 2 LAB",
        "description": "Based on Air Enthalpy Test Method with Capacity Range up to 3.5 TR, and airflow from 200 to 1750 CFM."
      },
      "item3": {
        "title": "PSYCHOMETRIC 3 LAB",
        "description": "Based on Air Enthalpy Test Method with Capacity Range up to 35 TR, and airflow from 1000 to 15000 CFM."
      },
      "item4": {
        "title": "CALORIMETER 1 LAB",
        "description": "Balanced Ambient Calorimetric Lab with Capacity Range up to 5 TR."
      }
    },
    "viewCertificate": "View Certificate",
    "downloadIsoCertificate": "Download ISO Certificate"
  },
  "ar": {
    "home": "الرئيسية",
    "aboutUs": "عن ",
    "title": "ميراكو",
    "info1": "عروض حصرية",
    "desc1": "أفضل العروض على أجهزة التكييف",
    "info2": "شحن مجاني",
    "desc2": "توصيل مجاني إلى باب منزلك",
    "info3": "تركيب مجاني",
    "desc3": "تركيب مجاني عند التسوق اونلاين",
    "info4": "اكتشف المنتج المناسب",
    "desc4": "احصل على المنتج الذي يناسب غرفتك",
    "info5": "تسهيلات الدفع",
    "desc5": "الدفع نقدًا أو عبر بطاقات الائتمان أو الخصم",
    "shopFrom": "مميزات الشراء من",
    "us": "ميراكو",
    "tabs": {
      "about": "نبذة عنا",
      "why": "لماذا ميراكو؟",
      "pcb": "مصنع لوحات الدوائر المطبوعة",
      "labs": "المختبرات",
      "ethics": "الأخلاقيات والامتثال"
    },
    "carrierText": "شركة Carrier هي الرائدة عالميًا في حلول المناخ والطاقة الذكية، مدفوعة بالابتكار ونهج يركز على العميل. مع فريق عالمي المستوى وثقافة تعتمد الأداء، نقدم قيمة طويلة الأمد ونقود السوق من خلال نمو استراتيجي.",
    "mideaText": "تأسست شركة Midea عام 1968، وتقدم مجموعة واسعة من الأجهزة المنزلية، متخصصة في معالجة الهواء والتبريد والغسيل والمطبخ والعناية بالأرضيات والإضاءة. ومعروفة عالميًا بابتكارها وجودتها، تستمر Midea في تحسين الحياة اليومية.",
    "quality": "الجودة",
    "policy": "السياسة",
    "qualityParagraph1": "نحن ملتزمون بتقديم حلول HVAC عالية الجودة تلبي توقعات العملاء في الوقت المحدد وضمن الميزانية، مع الامتثال لجميع اللوائح.",
    "qualityParagraph2": "من خلال إشراك فريقنا وتطبيق المعايير الدولية واعتناق التحسين المستمر، نقود الكفاءة والاستدامة والنجاح طويل الأمد، مع الحفاظ على مكانتنا كمزود رائد في الصناعة.",
    "pcb": "لوحات الدوائر المطبوعة",
    "factory": "المصنع",
    "pcbParagraph1": "نطمح إلى إنتاج لوحات التحكم الإلكترونية ذات أعلى جودة. نسعى لأن نكون مصنعًا من الطراز الأول يخدم الشرق الأوسط وأفريقيا.",
    "pcbParagraph2": "تأسس مصنع لوحات الدوائر المطبوعة الخاص بميراكو في فبراير 2022 ليكون قاعدة التصنيع للمشروع المشترك مع Midea في مصر، متخصصًا في لوحات التحكم والعرض الإلكترونية. يتضمن المرفق غرف نظيفة بدرجة حرارة ورطوبة ثابتة بقيمة 300,000، بالإضافة إلى أرضية مضادة للكهرباء الساكنة. وهو مزود بخطوط إنتاج SMT آلية بالكامل، وخطوط إدراج آلية، وخطين لإنتاج DIP.",
    "pcbParagraph3": "نحن نعطي الأولوية لتحسين القيمة ونلتزم بالتطوير الهندسي المستمر لتحقيق رضا العملاء ومتطلبات التسليم.",
    "pcbParagraph4": "نشارك في أنشطة تجارية متنوعة مسترشدين بأربعة ركائز استراتيجية للريادة التكنولوجية.",
    "labs": "المختبرات",
    "history": "التاريخ",
    "labsParagraph": "نفخر بكوننا أول مختبر معتمد ISO 17025 في مصر لاختبار أجهزة التكييف منذ عام 2005. تدعم مختبراتنا الخبرية البحث والتطوير ومراقبة الجودة من خلال اختبار عينات عشوائية عبر خطوط إنتاج Miraco. نضمن أن كل وحدة تفي بأعلى معايير الكفاءة والسلامة والأداء المستمر على مدار الساعة طوال العام.",
    "timeline1992Title": "نقطة البداية",
    "timeline1992Desc": "تم إنشاء مختبرات Miraco لخدمة اختبار وحدات التكييف",
    "timeline2005Title": "الاعتماد",
    "timeline2005Desc": "أول مختبرات معتمدة ISO 17025 في مصر من قبل EGAC",
    "timeline2012Title": "المختبرات",
    "timeline2012Desc": "أول مختبرات في مصر للحصول على شهادة اعتراف ESMA",
    "timeline2017Title": "مختبرات المعايرة والسلامة",
    "timeline2017Desc": "قدمنا مختبرات المعايرة وسلامة المنتج بناءً على معايير IEC 60335-1&2",
    "aboutSlider": {
      "item1": {
        "title": "ما يدفعنا",
        "description": "من خلال استغلال قوتنا الأساسية، نقدم خدمات اختبار أجهزة التكييف والمعايرة والسلامة في Miraco — مع التزام صارم بأفضل معايير الجودة وأدنى هامش للخطأ، وخبرة فريقنا المتخصص باستخدام أحدث التقنيات."
      },
      "item2": {
        "title": "ما يدفعنا",
        "description": ""
      },
      "item3": {
        "title": "",
        "description": ""
      }
    },

    "our": "مخاعيرنا",
    "ehs": "الصحة والسلامة والبيئة",
    "standards": "المعايير",
    "testing": "الاختبار",
    "downloadTestingDocs": "تحميل مستندات الاختبار",
    "calibration": "المعايرة",
    "downloadCalibrationDocs": "تحميل مستندات المعايرة",
    "safety": "السلامة",
    "downloadSafetyDocs": "تحميل مستندات السلامة",

    "ethics": "الأخلاقيات و",
    "compliance": "الامتثال",
    "ethicsParagraph1": "شركة Miraco ملتزمة بتطبيق أعلى معايير الأخلاقيات والامتثال ومكافحة الفساد والرشاوى وغسيل الأموال. يشمل برنامج الأخلاقيات والامتثال لدينا برنامج الإبلاغ عن الانتهاكات وضابط أخلاقيات وامتثال مسؤول عن التدريب والتحقيقات وإدارة مخاطر المؤسسة.",
    "ethicsParagraph2": "نتوقع من موظفينا أداء عملهم بطريقة قانونية وأخلاقية وآمنة. نبني الثقة، نظهر الاحترام، نتصرف بنزاهة، ونحمي سمعة الشركة ونموها.",
    "codeEthicsEn": "مدونة الأخلاقيات – إنجليزي",
    "ethicsPdfEn": "/pdfs/code-of-ethics-en.pdf",
    "codeEthicsAr": "مدونة الأخلاقيات – عربي",
    "ethicsPdfAr": "/pdfs/code-of-ethics-ar.pdf",

    "ehsPolicy": "سياسة الصحة والسلامة والبيئة",
    "ehsParagraph1": "في Miraco، سلامة موظفينا والبيئة هي أولويتنا القصوى. نحن ملتزمون بالحفاظ على بيئة عمل خالية من المخاطر والتلوث، حيث يكون الموظفون في أمان، وتكون منتجاتنا وخدماتنا آمنة ومسؤولة بيئيًا.",
    "ehsParagraph2": "لتحقيق ذلك، يتعين على كل وحدة أعمال في Miraco بمصر:",
    "ehsBullet1": "الامتثال لجميع قوانين ولوائح ومعايير EHS المحلية والمؤسسية",
    "ehsBullet2": "تعزيز ثقافة السلامة والمسؤولية البيئية من خلال مشاركة الموظفين الفعّالة",
    "ehsBullet3": "تقييم ومنع والسيطرة على مخاطر السلامة والبيئة بشكل منهجي",
    "ehsBullet4": "دمج أهداف EHS في التخطيط الاستراتيجي للأعمال",
    "ehsBullet5": "تخصيص الموارد لدعم ومراقبة وتحميل المسؤولية عن أداء EHS",
    "ehsBullet6": "التحقيق في الحوادث واتخاذ الإجراءات التصحيحية والوقائية",
    "ehsBullet7": "منع التلوث وفقًا لـ ISO 14001:2015",
    "ehsBullet8": "التحسين المستمر لتقليل المخاطر والأثر البيئي",

    "certifiedInPrefix": "نفخر بشهادة",
    "certifiedInSuffix": "",
    "iso9001Title": "ISO 9001:2015",
    "iso9001Subtitle": "إدارة الجودة",
    "iso14001Title": "ISO 14001:2015",
    "iso14001Subtitle": "الإدارة البيئية",
    "iso45001Title": "ISO 45001:2018",
    "iso45001Subtitle": "الصحة والسلامة المهنية",
    "iso17025Title": "ISO/IEC 17025:2017",
    "iso17025Subtitle": "الاعتماد المخبري",
    "iso27001Title": "ISO/IEC 27001:2013",
    "iso27001Subtitle": "إدارة أمن المعلومات",

    "drivenBy": "تدفعنا",
    "drivenByItems": {
      "item1": {
        "title": "الهدف الذي نخدمه",
        "description": "هو الحفاظ على الأداء المتميز للصحة والسلامة والبيئة من حيث تقليل عدد الإصابات وتعزيز صحة موظفينا ومنع أي تلوث بيئي."
      },
      "item2": { "title": "", "description": "" },
      "item3": { "title": "", "description": "" },
      "item4": { "title": "", "description": "" }
    },

    "labsItems": {
      "item1": {
        "title": "مختبر قياس النفاذية 1",
        "description": "يعتمد على طريقة قياس إنثالبي الهواء بقدرة تصل إلى 5 TR وتدفق هواء من 200 إلى 2200 CFM."
      },
      "item2": {
        "title": "مختبر قياس النفاذية 2",
        "description": "يعتمد على طريقة قياس إنثالبي الهواء بقدرة تصل إلى 3.5 TR وتدفق هواء من 200 إلى 1750 CFM."
      },
      "item3": {
        "title": "مختبر قياس النفاذية 3",
        "description": "يعتمد على طريقة قياس إنثالبي الهواء بقدرة تصل إلى 35 TR وتدفق هواء من 1000 إلى 15000 CFM."
      },
      "item4": {
        "title": "مختبر المقياس الحراري 1",
        "description": "مختبر متوازن بقدرة تصل إلى 5 TR."
      }
    },
    "viewCertificate": "عرض الشهادة",
    "downloadIsoCertificate": "تحميل الشهادة"
  }
}
</i18n>

<style lang="postcss" scoped>
.terms__grid {
  @screen lg {
    @apply mx-auto;
  }
}

.bullet-list {
  position: relative;
  &::before {
    @apply absolute top-0 left-0 right-4 h-full bg-secondary-300;
    content: '';
    border-radius: 20px;
    z-index: -1;
    @screen lg {
      border-radius: 20px 20px 20px 20px;
    }
  }
}

.bullet-description {
  max-width: calc(100% - 100px);
}

.bg-coloration-secondary {
  background-image: linear-gradient(to top, #1ba4e6, rgba(27, 164, 230, 0.7));
}

.slider-container {
  max-width: 1366px;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
